import { INTERESTS } from './contact-interests';

// Production-only telemetry. Never send form contents or contact details to GA4.
const PRODUCTION_HOSTS = new Set(['forgedopshq.com', 'www.forgedopshq.com']);
const confirmedLeadIds = new Set();
const serviceNames = new Map(INTERESTS.map(name => [name.toLocaleLowerCase(), name]));
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
const isGtagAvailable = () => typeof window !== 'undefined'
  && PRODUCTION_HOSTS.has(window.location.hostname) && typeof window.gtag === 'function';
const approvedService = value => serviceNames.get(String(value || '').toLocaleLowerCase()) || 'Unspecified';

// Telemetry must never turn an accepted inquiry into a failed form submission.
function sendEvent(name, params) {
  try {
    window.gtag('event', name, params);
    return true;
  } catch {
    return false;
  }
}

function safePageLocation() {
  const url = new URL(window.location.href);
  url.hash = '';
  const query = new URLSearchParams();
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_content']) {
    const value = url.searchParams.get(key);
    if (value && value.length <= 100 && !/[@<>\r\n]/.test(value)) query.set(key, value);
  }
  const interest = url.searchParams.get('interest') || url.searchParams.get('service');
  if (interest && approvedService(interest) !== 'Unspecified') query.set('interest', approvedService(interest));
  url.search = query.toString();
  return url;
}

/** One manual SPA page view. Unknown query parameters and checkout IDs stay out of GA4. */
export function trackPageView() {
  if (!isGtagAvailable()) return false;
  const url = safePageLocation();
  return sendEvent('page_view', {
    page_path: url.pathname + url.search,
    page_location: url.href,
    page_title: document.title,
  });
}

/** Site-defined CTA labels only, never customer input. */
export function trackCtaClick(label, location) {
  if (!isGtagAvailable()) return false;
  return sendEvent('cta_click', { cta_label: label, cta_location: location });
}

/** Only count an accepted, persisted contact response; never count a click as a lead. */
export function trackLead(service, leadId) {
  if (!isGtagAvailable() || !UUID.test(String(leadId || '')) || confirmedLeadIds.has(leadId)) return false;
  const sent = sendEvent('generate_lead', {
    method: 'contact_form',
    service_interest: approvedService(service),
    event_id: leadId,
  });
  if (!sent) return false;
  confirmedLeadIds.add(leadId);
  // Bound memory without adding persistent browser storage.
  if (confirmedLeadIds.size > 100) confirmedLeadIds.delete(confirmedLeadIds.values().next().value);
  return true;
}

/** Contact intent is useful, but does not establish that a call/email became a lead. */
export function trackContactClick(method) {
  if (!isGtagAvailable() || !['phone', 'email'].includes(method)) return false;
  return sendEvent('contact_click', { contact_method: method });
}
